// Where a value comes from: ⓘ next to a row opens what it means, the
// source and its date, how it is measured, and links to the record and the
// dataset. Rendered in a portal so table overflow cannot clip it; closes on
// an outside click, Escape, scroll or resize.
//
// 2026-10-12: titled by the row's label instead of the internal field key;
// links to the record itself (utils/sourceLinks) when the site holds its key;
// dataset details come from the backend registry, fetched once per source.
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FIELD_PROVENANCE, sourceIdFor, fetchSource } from '../utils/fieldProvenance';
import { recordLink } from '../utils/sourceLinks';
import { monthsSince } from '../utils/format';
import './ProvenancePopover.css';

const POP_W = 360;

// The site the values on screen belong to, so every ⓘ can link to its
// records without each row passing it (VerifiedSiteData, SelectedSiteCard).
export const ProvenanceSite = createContext(null);

export function ProvenanceIcon({ field, label = null, site = null, country = 'NO', dataDate = null }) {
  const contextSite = useContext(ProvenanceSite);
  site = site || contextSite;
  const btnRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    if (!anchor) return undefined;
    const close = () => setAnchor(null);
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || e.target.closest?.('.prov-pop')) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [anchor]);

  const onToggle = (e) => {
    e.stopPropagation();
    setAnchor(anchor ? null : e.currentTarget.getBoundingClientRect());
  };

  return (
    <>
      <button ref={btnRef} type="button" className="pp-info-icon" onClick={onToggle}
              aria-label={`Source of ${label || 'this value'}`}>ⓘ</button>
      {anchor && <ProvenancePopover field={field} label={label} site={site} country={country}
                                    dataDate={dataDate} anchor={anchor} />}
    </>
  );
}

function ProvenancePopover({ field, label, site, country, dataDate, anchor }) {
  const meta = FIELD_PROVENANCE[field] || {};
  const sourceId = sourceIdFor(field, site, country);
  const record = meta.record ? recordLink(meta.record, site) : null;
  const also = typeof meta.also === 'function' ? meta.also(site) : [];
  const [source, setSource] = useState(null);
  useEffect(() => {
    let live = true;
    fetchSource(sourceId).then(s => { if (live) setSource(s); });
    return () => { live = false; };
  }, [sourceId]);

  // Below the icon, or above when there is more room there.
  const margin = 8;
  const left = Math.min(Math.max(margin, anchor.left + anchor.width / 2 - POP_W / 2), window.innerWidth - POP_W - margin);
  const below = window.innerHeight - anchor.bottom - margin;
  const above = anchor.top - margin;
  const pos = below < 240 && above > below
    ? { bottom: window.innerHeight - anchor.top + 6, maxHeight: above - 12 }
    : { top: anchor.bottom + 6, maxHeight: below - 12 };

  const months = monthsSince(record?.published);
  const published = record?.published
    ? `${record.published}${record.publisher ? ` by ${record.publisher}` : ''}${months != null && months >= 12 ? ` · ${months} months ago` : ''}`
    : dataDate;
  const facts = [
    ['Source', source?.name
      ? <a href={source.homepageUrl} target="_blank" rel="noopener noreferrer">{source.name}</a>
      : meta.source],
    ['Published', published, months != null && months >= 12],
    ['Method', meta.method],
    ['Search radius', meta.radiusKm != null ? `${meta.radiusKm} km` : null],
    ['Coverage', meta.dataset],
    ['Licence', source?.license && (source.licenseUrl
      ? <a href={source.licenseUrl} target="_blank" rel="noopener noreferrer">{source.license}</a>
      : source.license)],
    ['Updated', !published ? source?.updateFrequency : null],
  ].filter(([, v]) => v != null && v !== '');
  const datasetUrl = source?.dataUrl || source?.homepageUrl;

  return createPortal(
    <div className="prov-pop" role="dialog" aria-label={`Source of ${label || field}`}
         style={{ left, width: POP_W, ...pos }}>
      <div className="prov-pop-title">{label || field}</div>
      {meta.meaning && <p className="prov-pop-meaning">{meta.meaning}</p>}
      {facts.length > 0 && (
        <dl className="prov-pop-facts">
          {facts.map(([k, v, warn]) => (
            <React.Fragment key={k}>
              <dt>{k}</dt>
              <dd className={warn ? 'is-warn' : undefined}>{v}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      {(record || datasetUrl || also.length > 0) && (
        <div className="prov-pop-links">
          {record && <a className="prov-pop-record" href={record.url} target="_blank" rel="noopener noreferrer">{record.label} ↗</a>}
          {also.map(l => <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer">{l.label} ↗</a>)}
          {datasetUrl && <a href={datasetUrl} target="_blank" rel="noopener noreferrer">About the dataset ↗</a>}
        </div>
      )}
      {!meta.meaning && facts.length === 0 && !record && (
        <p className="prov-pop-meaning prov-pop-empty">No source recorded for this value yet.</p>
      )}
    </div>,
    document.body,
  );
}
